'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { UserAvatar } from '@/components/images/user-avatar';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { formatCountFor } from '@/lib/format/format-count';
import type { TopBackerSummary } from '@/lib/launchpad/derive-fake-fields';
import { cn } from '@/lib/ui/utils';
export interface ProposalBackerStackProps {
  backers: TopBackerSummary[];
  totalCount: number;
  /** Mobile/tablet label rendered below the count; desktop gets its table header. */
  responsiveLabel?: string;
  animateValuesOnMount?: boolean;
}
export interface BackerAvatarPileProps {
  backers: TopBackerSummary[];
  className?: string | undefined;
}
const MAX_AVATARS = 3;

/** Overlapping avatar pile of the top backers (or empty when there are none). */
export function BackerAvatarPile(t0) {
  const $ = _c(12);
  const {
    backers,
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== backers || $[1] !== className) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visible = backers.slice(0, MAX_AVATARS);
      if (visible.length === 0) {
        t4 = null;
        break bb0;
      }
      if ($[6] !== className) {
        t1 = cn("flex items-center", className);
        $[6] = className;
        $[7] = t1;
      } else {
        t1 = $[7];
      }
      t2 = "proposal-backer-avatar-pile";
      t3 = visible.map(_temp);
    }
    $[0] = backers;
    $[1] = className;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t5 = <div className={t1} data-testid={t2}>{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp(backer, idx) {
  return <span key={backer.walletAddress} className={cn(idx > 0 && "-ml-3.5 lg:-ml-3")}><UserAvatar className="size-7 lg:size-6" size="sm" userId={backer.walletAddress} displayName={backer.displayName} avatarUrl={backer.avatarUrl} initialsMaxChars={1} nsfw={backer.avatarNsfw} rasterise={false} borderWidth={1} borderColor="border-background-primary" /></span>;
}
export function ProposalBackerStack(t0) {
  const $ = _c(24);
  const {
    backers,
    totalCount,
    responsiveLabel,
    animateValuesOnMount: t1
  } = t0;
  const animateValuesOnMount = t1 === undefined ? true : t1;
  const t2 = useLocale();
  let t3;
  if ($[0] !== t2) {
    t3 = formatCountFor(t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const formatCount = t3;
  let t4;
  if ($[2] !== backers || $[3] !== responsiveLabel || $[4] !== totalCount) {
    t4 = totalCount > 0 ? <BackerAvatarPile backers={backers} className={responsiveLabel === undefined ? undefined : "order-2 lg:order-1"} /> : null;
    $[2] = backers;
    $[3] = responsiveLabel;
    $[4] = totalCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = responsiveLabel !== undefined && "order-1 flex flex-col items-end lg:order-2 lg:block";
  let t6;
  if ($[6] !== t5) {
    t6 = cn(t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = responsiveLabel !== undefined && "leading-none lg:leading-normal";
  let t8;
  if ($[8] !== t7) {
    t8 = cn("text-body font-medium text-foreground-primary", t7);
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== animateValuesOnMount || $[11] !== formatCount || $[12] !== t8 || $[13] !== totalCount) {
    t9 = <AnimatedNumber value={totalCount} format={formatCount} animateOnMount={animateValuesOnMount} className={t8} />;
    $[10] = animateValuesOnMount;
    $[11] = formatCount;
    $[12] = t8;
    $[13] = totalCount;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== responsiveLabel) {
    t10 = responsiveLabel === undefined ? null : <span className="mt-0.5 text-caption leading-none text-foreground-secondary lg:hidden">{responsiveLabel}</span>;
    $[15] = responsiveLabel;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t6 || $[19] !== t9) {
    t11 = <div className={t6}>{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t6;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t4) {
    t12 = <div className="flex items-center gap-2" data-testid="proposal-backer-stack">{t4}{t11}</div>;
    $[21] = t11;
    $[22] = t4;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}